import { rufe } from './client';
import type { Kundenart } from './kunden';

/**
 * Der Zugang zum Produktkatalog (Lastenheft Kapitel 5).
 *
 * Wie bei den Kunden spiegeln die Typen den VERTRAG der API und nicht die
 * Tabellen, und dieselbe Eigenheit zieht sich durch: Felder mit einer
 * Berechtigung nach SYS-03 sind OPTIONAL und FEHLEN, wenn der angemeldete
 * Benutzer sie nicht sehen darf. Die Oberflaeche prueft mit `in` und nicht
 * auf einen Wert — `null` hiesse "es gibt keinen", nicht "Sie duerfen nicht".
 *
 * BETRAEGE SIND ZEICHENKETTEN. Der Rechenkern fuehrt sie als Festkomma auf
 * `bigint`; `number` verlaesst ab vierstelligen Betraegen den sicheren
 * Ganzzahlbereich. Gerechnet wird in der Oberflaeche mit keinem davon.
 */

export type Produktart =
  | 'fahrtraining'
  | 'event'
  | 'verpflegung'
  | 'ware'
  | 'dienstleistung'
  | 'baustein'
  | 'ressource'
  /* C18 (0274): der Wertgutschein als buchbarer Artikel. */
  | 'gutschein';

export const PRODUKTARTNAME: Record<Produktart, string> = {
  fahrtraining: 'Fahrtraining',
  event: 'Event',
  verpflegung: 'Verpflegung',
  ware: 'Ware',
  dienstleistung: 'Dienstleistung',
  baustein: 'Baustein',
  ressource: 'Ressource',
  gutschein: 'Gutschein',
};

export type Artikelstatus = 'aktiv' | 'auslaufend' | 'gesperrt';

export const STATUSNAME: Record<Artikelstatus, string> = {
  aktiv: 'aktiv',
  auslaufend: 'auslaufend',
  gesperrt: 'gesperrt',
};

/**
 * PRD-41, PRD-42. `zusatzleistung` seit AA-009 (31.08.2026): Die vier
 * uebrigen beschreiben, wie Trainings- oder Ressourcenkapazitaet verkauft
 * wird — ein Kaffee ist nichts davon.
 */
export type Vertriebsform =
  | 'einzelplatz'
  | 'exklusivtermin'
  | 'baustein'
  | 'ressource'
  | 'zusatzleistung';

export const VERTRIEBSFORMNAME: Record<Vertriebsform, string> = {
  einzelplatz: 'Einzelplatz',
  exklusivtermin: 'Exklusivtermin',
  baustein: 'Baustein',
  ressource: 'Ressourcenüberlassung',
  zusatzleistung: 'Zusatzleistung',
};

/** BUC-84: die Preisstellung der Zusatzleistung (AA-009). */
export type Zusatzleistungsbezug = 'buchung' | 'teilnehmer';

export const BEZUGSNAME: Record<Zusatzleistungsbezug, string> = {
  teilnehmer: 'je Teilnehmer',
  buchung: 'je Buchung',
};

export type Mengeneinheit =
  | 'Stk'
  | 'Platz'
  | 'Termin'
  | 'Std'
  | 'Tag'
  | 'Block'
  | 'Person'
  | 'Pauschal';

export type Rangstufe =
  | 'kundenindividuell'
  | 'kundengruppe'
  | 'aktion'
  | 'vertriebsform'
  | 'standard';

export const RANGSTUFENNAME: Record<Rangstufe, string> = {
  kundenindividuell: 'Kundenindividuell',
  kundengruppe: 'Kundengruppe',
  aktion: 'Aktion',
  vertriebsform: 'Vertriebsform',
  standard: 'Standard',
};

/**
 * Ein Standort am Produkt (PRD-01 i. d. F. AeA-39, Entwurf, Auftrag #22). Ein
 * abgeschalteter bleibt stehen, bis jemand ihn entfernt, wird aber keinem
 * neuen Termin mehr vorgegeben oder angeboten.
 */
export type Produktstandort = {
  readonly kennung: string;
  readonly bezeichnung: string;
  readonly aktiv: boolean;
};

/** Ein vererbbarer Wert mit seiner Herkunft (PRD-02). */
export type Wirksam = {
  readonly wert: string | null;
  /**
   * Vier Zustaende seit AA-008 Punkt 13: Zwischen "am Artikel" und "von der
   * Warengruppe" steht "vom Stammprodukt" (PRD-31). Eine Variante ohne
   * eigenen Wert folgt ihrem Stamm — auch wenn er sich spaeter aendert.
   */
  readonly herkunft: 'artikel' | 'stammprodukt' | 'warengruppe' | 'keiner';
  readonly gruppe: string | null;
  readonly gruppenname: string | null;
};

export type Artikelzeile = {
  readonly id: string;
  readonly artikelnummer: string;
  readonly bezeichnung: string;
  readonly produktart: Produktart;
  readonly mengeneinheit: Mengeneinheit;
  readonly status: Artikelstatus;
  readonly warengruppe: string | null;
  readonly warengruppenname: string | null;
  readonly steuerschluessel: string | null;
  readonly guete: number | null;
  /** PRD-03: ob der Artikel in einem NEUEN Beleg waehlbar ist. */
  readonly waehlbar: boolean;
  /** BUC-11 (ÄA-55): ob das Produkt eine Warteliste anbietet. Ältere Antworten tragen es nicht. */
  readonly warteliste?: boolean;
  /**
   * PRD-11: ob heute ueberhaupt ein Preis gilt.
   *
   * ZWEI VERSCHIEDENE AUSSAGEN, und das Zusammenwerfen kostete am
   * 31.08.2026 einen Anwender einen Fehlversuch: Ein aktiver Artikel ohne
   * gepflegten Preis ist waehlbar und laesst sich trotzdem nicht in ein
   * Angebot nehmen — ANG-05 findet nichts.
   */
  readonly hatPreis: boolean;
  /**
   * PRD-01, BUC-01 i. d. F. AeA-39 (Entwurf, Auftrag #22): die Standorte des
   * Produkts. Genau ein aktiver ist die Vorgabe für den Ort neuer Termine,
   * mehrere bietet die Terminmaske zur Wahl an.
   */
  readonly standorte: readonly Produktstandort[];
  /**
   * Wie oft dieser Artikel in den letzten zwölf Monaten auf einem Beleg stand.
   *
   * Angebot und Auftrag zusammen (ANG-02, AUF-03). Die Auswahl im Beleg ordnet
   * danach und zeigt die Zahl an der Zeile — wer eine Position erfasst, greift
   * in neun von zehn Fällen zu demselben halben Dutzend.
   */
  readonly verwendungen: number;
  /**
   * PRD-41: in welchen Formen das Produkt verkauft werden darf.
   *
   * Die Belegmaske braucht sie beim Erfassen: Ein Produkt mit mehr als einer
   * Form verlangt eine Wahl, eines mit genau einer nicht.
   */
  readonly vertriebsformen: readonly Vertriebsform[];
};

export type Artikel = {
  readonly id: string;
  readonly artikelnummer: string;
  readonly bezeichnung: string;
  readonly kurztext: string | null;
  readonly langtext: string | null;
  readonly mengeneinheit: Mengeneinheit;
  readonly produktart: Produktart;
  readonly warengruppe: string | null;
  readonly warengruppenname: string | null;
  readonly status: Artikelstatus;
  readonly vertriebsformen: readonly Vertriebsform[];
  /** BUC-105: Welchen Kundenarten dieser Artikel angeboten wird. */
  readonly angebotenAn: readonly Kundenart[];
  /** LEG-16: null heisst "noch nicht geklaert" (C04). Aeltere Antworten tragen das Feld nicht. */
  readonly widerrufsrecht?: 'besteht' | 'besteht_nicht' | null;
  /** C18 (0274): beim Gutscheinartikel Art und Betrag -- fest ODER von/bis. */
  readonly gutschein?: {
    readonly art: string | null;
    readonly betrag: string | null;
    readonly von: string | null;
    readonly bis: string | null;
  } | null;
  readonly tarif: 'standard' | 'flex';
  readonly mindestdauerBloecke: number | null;
  /** BUC-84, AA-009. Gesetzt genau dann, wenn `zusatzleistung` dabei ist. */
  readonly bezugZusatzleistung: Zusatzleistungsbezug | null;
  readonly bestand: number | null;
  /** Tabelle 27, Zeile 2 (0252): so viele Tage vor Beginn wird ein Termin von selbst buchbar. */
  readonly verkaufsstartTage: number | null;
  /** BUC-01: ob das Produkt in der Buchungsstrecke steht. */
  readonly buchbar: boolean;
  /** BUC-11 (ÄA-55): ob Interessenten sich auf die Warteliste setzen können. */
  readonly warteliste: boolean;
  readonly wiederbeschaffungTage: number | null;
  /**
   * PRD-01, BUC-01 i. d. F. AeA-39 (Entwurf, Auftrag #22): die Standorte des
   * Produkts. Genau ein aktiver ist die Vorgabe für den Ort neuer Termine,
   * mehrere bietet die Terminmaske zur Wahl an.
   */
  readonly standorte: readonly Produktstandort[];
  readonly geloescht: boolean;
  readonly angelegtAm: string;
  readonly stand: string;
  readonly eigen: {
    readonly steuerschluessel: string | null;
    readonly erloeskontoInland: string | null;
    readonly erloeskontoEu: string | null;
    readonly erloeskontoDrittland: string | null;
    /** PRF-11. `'0.00'` heisst "kein Rabatt", `null` "von der Warengruppe". */
    readonly hoechstrabattProzent: string | null;
  };
  readonly wirksam: {
    readonly steuerschluessel: Wirksam;
    readonly erloeskontoInland: Wirksam;
    readonly erloeskontoEu: Wirksam;
    readonly erloeskontoDrittland: Wirksam;
    readonly hoechstrabattProzent: Wirksam;
  };
  /** PRD-18, SYS-03: fehlt ganz ohne `artikel/einkaufspreis_sehen`. */
  readonly einkaufspreis?: string | null;

  /** PRD-31: Eine Variante IST ein Artikel — eigene Nummer, eigener Preis. */
  readonly varianteVon: string | null;
  readonly stammprodukt: string | null;
  readonly fahrzeugart: Fahrzeugart | null;
  readonly umfang: Umfang | null;
  readonly zielgruppe: Zielgruppe | null;
  readonly varianten: readonly Variantenzeile[];

  /** PRD-32, aufgeloest ueber das Stammprodukt. */
  readonly voraussetzungen: Voraussetzungen | null;

  /** PRD-25. `null` bei jeder anderen Produktart als `verpflegung`. */
  readonly verpflegung: Verpflegung | null;

  /**
   * PRD-39: Was dieses Produkt dem Kunden gegenüber enthält.
   *
   * Abgeleitet aus der Bausteinfolge, nie getippt. Eine MENGE und keine
   * Reihenfolge — die Tagesplanung darf innerhalb einer Phase tauschen
   * (TAG-07), und ein Verkaufstext, der einen Ablauf verspräche, wäre nicht
   * zu halten.
   */
  readonly leistungsmerkmale: readonly string[];

  /**
   * DAT-22, PRD-37: die geltende Fassung dieses Produkts.
   *
   * `null` bei einem Produkt ohne Inhalt. Ein Termin behält die Fassung, die
   * bei seiner Anlage galt — eine spätere Änderung wirkt nicht zurück.
   */
  readonly fassung: {
    readonly nummer: number;
    readonly gueltigAb: string;
  } | null;
};

/** PRD-31. Im Lastenheft abschliessend aufgezaehlt. */
export type Fahrzeugart = 'pkw' | 'transporter' | 'motorrad' | 'lkw';
export type Umfang = 'halbtags' | 'ganztags' | 'zweitaegig';
export type Zielgruppe = 'privat' | 'firma';

export const FAHRZEUGARTNAME: Readonly<Record<Fahrzeugart, string>> = {
  pkw: 'Pkw',
  transporter: 'Transporter',
  motorrad: 'Motorrad',
  lkw: 'Lkw',
};
export const UMFANGNAME: Readonly<Record<Umfang, string>> = {
  halbtags: 'halbtags',
  ganztags: 'ganztags',
  zweitaegig: 'zweitägig',
};
export const ZIELGRUPPENNAME: Readonly<Record<Zielgruppe, string>> = {
  privat: 'Privat',
  firma: 'Firma',
};

export type Variantenzeile = {
  readonly id: string;
  readonly artikelnummer: string;
  readonly bezeichnung: string;
  readonly fahrzeugart: Fahrzeugart | null;
  readonly umfang: Umfang | null;
  readonly zielgruppe: Zielgruppe | null;
  readonly geloescht: boolean;
};

/**
 * PRD-32. `herkunft` sagt, ob die Werte am Artikel selbst stehen oder vom
 * Stammprodukt kommen — ohne diese Angabe sieht ein geerbter Wert aus wie ein
 * gepflegter, und beim naechsten Gesetzesstand aendert jemand den falschen.
 */
export type Voraussetzungen = {
  readonly mindestalter: number | null;
  readonly fahrerlaubnisklassen: readonly string[];
  readonly vorkenntnisse: string | null;
  readonly fahrzeugMitbringen: boolean;
  readonly fahrzeugHinweis: string | null;
  readonly herkunft: 'artikel' | 'stammprodukt';
};

export type Verpflegung = {
  readonly portionen: number;
  readonly vorlaufStunden: number;
  readonly bistro: string | null;
  readonly bistroName: string | null;
  readonly plaetzeJePortion: number | null;
  readonly auswahl: readonly Verpflegungswahl[];
};

export type Verpflegungswahl = {
  readonly id: string;
  readonly bezeichnung: string;
  readonly hinweis: string | null;
  readonly istVorgabe: boolean;
  readonly reihenfolge: number;
};

export type Warengruppe = {
  readonly id: string;
  readonly schluessel: string;
  readonly bezeichnung: string;
  readonly oberGruppe: string | null;
  /** Stufe im Baum, 0 für eine Wurzelgruppe (PRD-02). */
  readonly tiefe: number;
  /** `null` heißt: von der Obergruppe geerbt (PRD-02). */
  readonly steuerschluessel: string | null;
  readonly erloeskontoInland: string | null;
  readonly erloeskontoEu: string | null;
  readonly erloeskontoDrittland: string | null;
  readonly standardtext: string | null;
  /** PRF-11: Höchstrabatt der Gruppe, als Text — nie durch das Gleitkomma. */
  readonly hoechstrabattProzent: string | null;
  readonly artikelzahl: number;
  readonly untergruppen: number;
};

/**
 * Was eine Warengruppe trägt (PRD-02, PRD-04, PRF-11).
 *
 * Alle Felder ausdrücklich: `null` heißt „nicht gesetzt" und damit „von der
 * Obergruppe erben". Ein weggelassenes Feld gibt es nicht — die Vererbung
 * unterscheidet nicht zwischen „leer" und „nicht genannt".
 */
export type Warengruppenfelder = {
  readonly bezeichnung: string;
  readonly oberGruppe: string | null;
  readonly steuerschluessel: string | null;
  readonly erloeskontoInland: string | null;
  readonly erloeskontoEu: string | null;
  readonly erloeskontoDrittland: string | null;
  readonly standardtext: string | null;
  readonly hoechstrabattProzent: string | null;
};

export type Steuerschluessel = {
  readonly schluessel: string;
  readonly bezeichnung: string;
  readonly prozent: string;
  readonly gueltigAb: string;
  readonly gueltigBis: string | null;
  /**
   * UNCL5305 (BT-118). `null` heißt: noch nicht entschieden — eine Rechnung
   * mit diesem Schlüssel lässt sich dann nicht als E-Rechnung ausgeben.
   */
  readonly steuerkategorie: string | null;
  /** BT-120: der Grund im Klartext, wenn keine Steuer ausgewiesen wird. */
  readonly befreiungsgrund: string | null;
  /** BT-121: derselbe Grund als VATEX-Code. */
  readonly befreiungsgrundCode: string | null;
};

export type Mengenstaffel = {
  readonly id: string;
  readonly abMenge: number;
  readonly einzelpreis: string;
};

export type Preiszeile = {
  readonly id: string;
  readonly preisliste: string;
  readonly preislistenkennung: string;
  readonly preislistenbezeichnung: string;
  readonly rangstufe: Rangstufe;
  readonly vertriebsform: Vertriebsform;
  readonly einzelpreis: string;
  readonly istBrutto: boolean;
  readonly gueltigAb: string;
  readonly gueltigBis: string | null;
  readonly staffeln: readonly Mengenstaffel[];
  readonly stand: string;
};

export type Preisliste = {
  readonly id: string;
  readonly kennung: string;
  readonly bezeichnung: string;
  readonly rangstufe: Rangstufe;
  readonly gueltigAb: string;
  readonly gueltigBis: string | null;
  readonly vorrangig: boolean;
  readonly preiszeilen: number;
  readonly zuordnungen: number;
  readonly stand: string;
};

export type Herleitungszeile = {
  readonly stufe: number;
  readonly regelart: string;
  readonly regelkennung: string;
  readonly wertDavor: string;
  readonly wertDanach: string;
  readonly ungerundet?: string;
  readonly gekappt?: true;
};

export type Preiskandidat = {
  readonly kennung: string;
  readonly rangstufe: Rangstufe;
  readonly gueltigAb: string;
  readonly gueltigBis: string | null;
  readonly vorrangig: boolean;
  readonly einzelpreis: string;
  readonly istBrutto: boolean;
  readonly gewaehlt: boolean;
};

export type Preisauskunft = {
  readonly artikel: string;
  readonly artikelnummer: string;
  readonly bezeichnung: string;
  readonly stichtag: string;
  readonly menge: number;
  readonly vertriebsform: Vertriebsform;
  readonly waehlbar: boolean;
  readonly kandidaten: readonly Preiskandidat[];
  readonly konflikt: { readonly kennungen: readonly string[]; readonly grund: string } | null;
  readonly preis: {
    readonly preisliste: string;
    readonly einzelpreis: string;
    readonly staffelpreis: string | null;
    readonly abMenge: number | null;
    readonly steuerschluessel: string;
    readonly steuerProzent: string;
    readonly zeilenbrutto: string;
    readonly netto: string;
    readonly steuerbetrag: string;
    /** Netto plus Steuer — der Betrag, den der Kunde zahlt. */
    readonly bruttoMitSteuer: string;
    /** PRD-12: ob der GEPFLEGTE Preis die Steuer schon enthaelt. */
    readonly istBrutto: boolean;
    /** RND-18: ob der Bruttobetrag mit diesem Satz darstellbar ist. */
    readonly darstellbar: boolean;
    /** Der gesamte Vorteil der Stufen 3 bis 7, im gepflegten Massstab. */
    readonly vorteil: string;
    readonly vorteilProzent: string;
    /** Der wirksame Hoechstrabatt, `null` wenn keiner gilt (PRF-11). */
    readonly hoechstrabattProzent: string | null;
    readonly herleitung: readonly Herleitungszeile[];
  } | null;
  /** Die angewandten Konditionen der Stufe 3 (PRD-15), in ihrer Wirkfolge. */
  readonly konditionen: readonly {
    readonly kennung: string;
    readonly bezeichnung: string;
    readonly art: 'prozent' | 'betrag';
    readonly wert: string;
    readonly bereich: 'artikel' | 'warengruppe' | 'alle';
    readonly nichtKombinierbar: boolean;
  }[];
  readonly vorbehalte: readonly string[];
  readonly deckungsbeitrag?: { readonly betrag: string; readonly prozent: string } | null;
};

/**
 * Eine gepflegte Kundenkondition — Stufe 3 der Preisfindung (PRD-15, Tab. 22).
 *
 * Träger ist der Kunde ODER die Rabattgruppe (genau eines), Geltungsbereich
 * der Artikel, die Warengruppe oder alles (höchstens eines). Beide
 * Bedingungen stehen an der Tabelle; die Namen kommen für die Anzeige mit,
 * damit die Maske sie nicht je Zeile nachladen muss.
 */
export type Rabattkondition = {
  readonly id: string;
  readonly kennung: string;
  readonly bezeichnung: string;
  readonly kunde: string | null;
  readonly kundenname: string | null;
  readonly rabattgruppe: string | null;
  readonly artikel: string | null;
  readonly artikelname: string | null;
  readonly warengruppe: string | null;
  readonly warengruppenname: string | null;
  readonly art: 'prozent' | 'betrag';
  /** Als Text — `numeric` darf keine Gleitkommazahl werden (RND-01). */
  readonly prozent: string | null;
  readonly betrag: string | null;
  /** PRF-08: verdrängt die übrigen Vorteile derselben und der folgenden Stufen. */
  readonly nichtKombinierbar: boolean;
  readonly gueltigAb: string;
  readonly gueltigBis: string | null;
  readonly geloescht: boolean;
};

export type Rabattkonditionsfelder = Omit<
  Rabattkondition,
  'id' | 'kundenname' | 'artikelname' | 'warengruppenname' | 'geloescht'
>;

export async function konditionen(
  filter: { readonly kunde?: string; readonly rabattgruppe?: string } = {},
): Promise<readonly Rabattkondition[]> {
  const teile = new URLSearchParams();
  if (filter.kunde !== undefined && filter.kunde !== '') teile.set('kunde', filter.kunde);
  if (filter.rabattgruppe !== undefined && filter.rabattgruppe !== '') {
    teile.set('rabattgruppe', filter.rabattgruppe);
  }
  const frage = teile.toString();
  return rufe(`/artikel/konditionen${frage === '' ? '' : `?${frage}`}`);
}

export async function konditionAnlegen(
  eingabe: Rabattkonditionsfelder,
): Promise<{ id: string }> {
  return rufe('/artikel/konditionen', { verfahren: 'POST', rumpf: eingabe });
}

/**
 * Eine Kondition beenden — es gibt kein Ändern.
 *
 * PRF-16: „Eine Preis- oder Konditionsänderung wirkt nie rückwirkend auf
 * bestehende Angebote, Aufträge, Buchungen oder Belege." Wer eine laufende
 * Kondition überschriebe, änderte rückwirkend, was in der Preisherleitung
 * schon steht. Der Weg ist deshalb: beenden und neu anlegen.
 */
export async function konditionBeenden(kondition: string, bis: string): Promise<void> {
  await rufe(`/artikel/konditionen/${kondition}/ende`, {
    verfahren: 'PUT',
    rumpf: { gueltigBis: bis },
  });
}

/**
 * Die Programmvorgaben eines Trainingsprodukts (PRD-23 i. d. F. ÄA-14).
 *
 * Konfiguration nach Tabelle 10: Die Administration pflegt sie selbst, „das
 * System wird ohne fest eingebaute Programme ausgeliefert" (E-74).
 *
 * `teilnehmerJeTrainer` gibt es nicht mehr — ein Termin trägt eine Gruppe und
 * einen betreuenden Trainer, die **Höchstgruppengröße ist damit zugleich der
 * Betreuungsschlüssel** (ÄA-14, TAG-15).
 */
export type Programmvorgabe = {
  /** AeA-34: Mindestteilnehmerzahl und Teilnehmerzahl (vorher Gruppengröße). */
  readonly mindestteilnehmer: number;
  readonly teilnehmerzahl: number;
  /** Zeitstunden ohne Pausen, als Text — nie durch das binäre Gleitkomma. */
  readonly trainingsdauerStunden: string | null;
  /** Der Schlüssel der am Trainer gepflegten Qualifikation (RES-54, TAG-14). */
  readonly trainerqualifikation: string | null;
  /** E-63: ein Hinweis für die Planung, vom System nicht ausgewertet. */
  readonly vorgabequelle: string | null;
  readonly vorgabefassung: string | null;
};

export async function programmvorgabe(
  artikel: string,
): Promise<{ readonly programmvorgabe: Programmvorgabe | null }> {
  return rufe(`/artikel/${artikel}/programmvorgabe`);
}

export async function programmvorgabeSetzen(
  artikel: string,
  eingabe: Programmvorgabe,
): Promise<void> {
  await rufe(`/artikel/${artikel}/programmvorgabe`, { verfahren: 'PUT', rumpf: eingabe });
}

/**
 * Die tatsächlich an Trainern gepflegten Qualifikationen.
 *
 * Aus dem Bestand und nicht aus einer Werteliste: Der Schlüssel ist an beiden
 * Enden Freitext, und ein Tippfehler heißt stillschweigend „kein Trainer
 * qualifiziert".
 */
/**
 * Die Qualifikationen, die ein Training verlangen kann (RES-54 i. d. F. ÄA-13).
 *
 * Aus der gepflegten Liste und nicht aus dem, was an Trainern steht: Der
 * Schlüssel wird Zeichen für Zeichen verglichen (TAG-14), und eine Auswahl,
 * die erst entsteht, nachdem jemand sie eingetippt hat, ist beim ersten
 * Produkt leer.
 */
export async function trainerqualifikationen(): Promise<
  readonly { readonly schluessel: string; readonly bezeichnung: string }[]
> {
  return rufe('/artikel/trainerqualifikationen');
}

/* ── Zusatzleistungen je Produkt und Pool (BUC-84, BUC-85 i. d. F. ÄA-31) ── */

export type Zusatzauswahl = 'frei' | 'vorausgewaehlt' | 'verpflichtend';

export type Zusatzangebot = {
  readonly hauptartikel: string;
  readonly zusatz: string;
  readonly artikelnummer: string;
  readonly bezeichnung: string;
  readonly bezug: Zusatzleistungsbezug;
  readonly auswahl: Zusatzauswahl;
  /** ÄA-31: ob der Zusatz auch in der Buchungsstrecke angeboten wird. */
  readonly buchungsstrecke: boolean;
  readonly reihenfolge: number;
  /** BUC-85: wie viele Ressourcen der Pool trägt; 0 heißt unbegrenzt. */
  readonly poolgroesse: number;
};

export type Poolressource = {
  readonly ressource: string;
  readonly kennung: string;
  readonly bezeichnung: string;
  readonly art: string;
};

export async function zusatzangebote(artikel: string): Promise<readonly Zusatzangebot[]> {
  return (await rufe<{ readonly angebote: readonly Zusatzangebot[] }>(
    `/artikel/${artikel}/zusatzangebote`,
  )).angebote;
}

export async function zusatzangebotSetzen(
  artikel: string,
  zusatz: string,
  eingabe: {
    readonly auswahl: Zusatzauswahl;
    readonly buchungsstrecke: boolean;
    readonly reihenfolge: number;
  },
): Promise<readonly Zusatzangebot[]> {
  return (await rufe<{ readonly angebote: readonly Zusatzangebot[] }>(
    `/artikel/${artikel}/zusatzangebote/${zusatz}`,
    { verfahren: 'PUT', rumpf: eingabe },
  )).angebote;
}

export async function zusatzangebotEntfernen(
  artikel: string,
  zusatz: string,
): Promise<readonly Zusatzangebot[]> {
  return (await rufe<{ readonly angebote: readonly Zusatzangebot[] }>(
    `/artikel/${artikel}/zusatzangebote/${zusatz}`,
    { verfahren: 'DELETE' },
  )).angebote;
}

export async function zusatzpool(zusatz: string): Promise<readonly Poolressource[]> {
  return (await rufe<{ readonly pool: readonly Poolressource[] }>(`/artikel/${zusatz}/zusatzpool`)).pool;
}

export async function zusatzpoolHinzufuegen(
  zusatz: string,
  ressource: string,
): Promise<readonly Poolressource[]> {
  return (await rufe<{ readonly pool: readonly Poolressource[] }>(
    `/artikel/${zusatz}/zusatzpool/${ressource}`,
    { verfahren: 'PUT' },
  )).pool;
}

export async function zusatzpoolEntfernen(
  zusatz: string,
  ressource: string,
): Promise<readonly Poolressource[]> {
  return (await rufe<{ readonly pool: readonly Poolressource[] }>(
    `/artikel/${zusatz}/zusatzpool/${ressource}`,
    { verfahren: 'DELETE' },
  )).pool;
}

/* ── Artikel ─────────────────────────────────────────────────────────────── */

export async function liste(filter: {
  readonly suche?: string;
  readonly produktart?: Produktart | '';
  readonly status?: Artikelstatus | '';
  readonly warengruppe?: string;
  readonly nurWaehlbar?: boolean;
  /** PRD-41: nur Artikel mit dieser Vertriebsform — etwa `zusatzleistung`. */
  readonly vertriebsform?: string;
  /** BUC-105: nur, was dieser Kundenart angeboten wird. */
  readonly fuerKundenart?: string;
  /**
   * Wonach geordnet wird. Ohne Angabe alphabetisch — die Ordnung des Stammes.
   * `haeufigkeit` ist die Ordnung des Belegs (13.09.2026).
   */
  readonly sortierung?: 'haeufigkeit';
}): Promise<{ readonly artikel: readonly Artikelzeile[]; readonly mehrAlsGezeigt: boolean }> {
  const fragen = new URLSearchParams();
  if (filter.suche) fragen.set('suche', filter.suche);
  if (filter.produktart) fragen.set('produktart', filter.produktart);
  if (filter.status) fragen.set('status', filter.status);
  if (filter.warengruppe) fragen.set('warengruppe', filter.warengruppe);
  if (filter.nurWaehlbar) fragen.set('nurWaehlbar', 'true');
  if (filter.vertriebsform !== undefined && filter.vertriebsform !== '') {
    fragen.set('vertriebsform', filter.vertriebsform);
  }
  if (filter.fuerKundenart !== undefined && filter.fuerKundenart !== '') {
    fragen.set('fuerKundenart', filter.fuerKundenart);
  }
  if (filter.sortierung !== undefined) fragen.set('sortierung', filter.sortierung);
  return rufe(`/artikel?${fragen.toString()}`);
}

export async function akte(id: string): Promise<Artikel> {
  return rufe(`/artikel/${id}`);
}

export type Anlage = {
  readonly bezeichnung: string;
  readonly produktart: Produktart;
  readonly mengeneinheit: Mengeneinheit;
  readonly kurztext: string | null;
  readonly langtext: string | null;
  readonly warengruppe: string | null;
  readonly steuerschluessel: string | null;
  readonly vertriebsformen: readonly Vertriebsform[];
  /** BUC-105. Fehlt es, setzt der Dienst alle Kundenarten. */
  readonly angebotenAn?: readonly Kundenart[];
  readonly tarif: 'standard' | 'flex';
  readonly mindestdauerBloecke: number | null;
  readonly einkaufspreis: string | null;
  /** BUC-84, AA-009: Pflicht bei der Vertriebsform „Zusatzleistung". */
  readonly bezugZusatzleistung: Zusatzleistungsbezug | null;
};

export async function anlegen(
  eingabe: Anlage,
): Promise<{ readonly id: string; readonly artikelnummer: string }> {
  return rufe(`/artikel`, { verfahren: 'POST', rumpf: eingabe });
}

/** Feldweises Sichern beim Verlassen des Feldes (M-12 sinngemaess). */
export async function feldSichern(
  id: string,
  feld: string,
  wert: string | number | boolean | null | readonly string[],
  stand: string,
): Promise<{ readonly stand: string }> {
  return rufe(`/artikel/${id}`, { verfahren: 'PATCH', rumpf: { stand, [feld]: wert } });
}

/**
 * Einen Standort ans Produkt nehmen oder davon wegnehmen (PRD-01 i. d. F.
 * AeA-39, Entwurf, Auftrag #22). Beides wiederholbar, ohne Stand.
 */
export async function standortHinzufuegen(artikel: string, standort: string): Promise<void> {
  await rufe(`/artikel/${artikel}/standorte/${encodeURIComponent(standort)}`, { verfahren: 'PUT' });
}

export async function standortEntfernen(artikel: string, standort: string): Promise<void> {
  await rufe(`/artikel/${artikel}/standorte/${encodeURIComponent(standort)}`, { verfahren: 'DELETE' });
}

/**
 * Die aktiven Standorte zur Auswahl am Produkt (PRD-01 i. d. F. AeA-39,
 * Entwurf). Unter `artikel/sehen`, nicht unter `termin/sehen` wie
 * `termine/standorte`.
 */
export async function standorte(): Promise<{
  readonly standorte: readonly { readonly kennung: string; readonly bezeichnung: string }[];
}> {
  return rufe('/artikel/standorte');
}

export async function warengruppen(): Promise<readonly Warengruppe[]> {
  return rufe(`/artikel/warengruppen`);
}

export async function warengruppeAnlegen(
  eingabe: Warengruppenfelder & { readonly schluessel: string },
): Promise<{ id: string }> {
  return rufe('/artikel/warengruppen', { verfahren: 'POST', rumpf: eingabe });
}

/**
 * Eine Warengruppe ändern — ohne den Schlüssel.
 *
 * Auf ihm steht die Navision-Zuordnung (FIB-04: Warengruppe →
 * Produktbuchungsgruppe). Wer ihn umbenennt, verschiebt die Zuordnung
 * stillschweigend auf die Auffangzeile „ohne".
 */
export async function warengruppeAendern(
  id: string,
  eingabe: Warengruppenfelder,
): Promise<void> {
  await rufe(`/artikel/warengruppen/${id}`, { verfahren: 'PATCH', rumpf: eingabe });
}

export async function warengruppeLoeschen(id: string): Promise<void> {
  await rufe(`/artikel/warengruppen/${id}`, { verfahren: 'DELETE' });
}

export async function steuerschluessel(): Promise<readonly Steuerschluessel[]> {
  return rufe(`/artikel/steuerschluessel`);
}

/* ── Preise ──────────────────────────────────────────────────────────────── */

export async function preise(
  artikel: string,
  auchBeendete = false,
): Promise<readonly Preiszeile[]> {
  return rufe(`/artikel/${artikel}/preise${auchBeendete ? '?auchBeendete=true' : ''}`);
}

export async function preisAnlegen(
  artikel: string,
  eingabe: {
    readonly preisliste: string;
    readonly vertriebsform: Vertriebsform;
    readonly einzelpreis: string;
    readonly istBrutto: boolean;
    readonly gueltigAb: string;
    readonly gueltigBis: string | null;
  },
): Promise<{ readonly id: string }> {
  return rufe(`/artikel/${artikel}/preise`, { verfahren: 'POST', rumpf: eingabe });
}

/**
 * Ein geltender Preis wird ABGELOEST und nicht geaendert (PRD-11, PRF-16).
 * Es gibt bewusst kein Gegenstueck, das den Betrag ueberschreibt.
 */
export async function preisFortschreiben(
  artikel: string,
  preis: string,
  eingabe: {
    readonly einzelpreis: string;
    readonly abDatum: string;
    readonly staffelnUebernehmen: boolean;
    /** Letzter Tag des Vorgaengers. `null` heisst "der Tag davor". */
    readonly endeVorgaenger: string | null;
  },
): Promise<{ readonly id: string; readonly beendet: string }> {
  return rufe(`/artikel/${artikel}/preise/${preis}/abloesung`, {
    verfahren: 'POST',
    rumpf: eingabe,
  });
}

export async function preisBeenden(
  artikel: string,
  preis: string,
  gueltigBis: string,
): Promise<void> {
  await rufe(`/artikel/${artikel}/preise/${preis}`, {
    verfahren: 'PATCH',
    rumpf: { gueltigBis },
  });
}

/** Der Staffelsatz als Ganzes (PRD-14). */
export async function staffelSetzen(
  artikel: string,
  preis: string,
  stufen: readonly { readonly abMenge: number; readonly einzelpreis: string }[],
): Promise<void> {
  // OHNE Grundpreis: Er kommt im Dienst aus fach.preis. Eine Schranke, der
  // der Aufrufer den Vergleichswert liefert, ist keine.
  await rufe(`/artikel/${artikel}/preise/${preis}/staffeln`, {
    verfahren: 'PUT',
    rumpf: { stufen },
  });
}

/**
 * Was eine Preisliste trägt (PRD-13, PRF-02 bis PRF-04).
 *
 * Ohne die Kennung: Unter ihr erscheint die Liste in der Preisherleitung
 * (PRF-13), und eine Herleitung, die auf eine umbenannte Liste zeigt, lässt
 * sich nicht mehr nachvollziehen.
 */
export type Preislistenfelder = {
  readonly bezeichnung: string;
  readonly rangstufe: Rangstufe;
  readonly gueltigAb: string;
  readonly gueltigBis: string | null;
  readonly vorrangig: boolean;
};

export async function preislisteAnlegen(
  eingabe: Preislistenfelder & { readonly kennung: string },
): Promise<{ id: string }> {
  return rufe('/artikel/preislisten', { verfahren: 'POST', rumpf: eingabe });
}

export async function preislisteAendern(
  id: string,
  eingabe: Preislistenfelder,
): Promise<void> {
  await rufe(`/artikel/preislisten/${id}`, { verfahren: 'PATCH', rumpf: eingabe });
}

export async function preislisteLoeschen(id: string): Promise<void> {
  await rufe(`/artikel/preislisten/${id}`, { verfahren: 'DELETE' });
}

export type Preislistenzuordnung = {
  readonly id: string;
  readonly kunde: string | null;
  readonly kundennummer: string | null;
  readonly kundenname: string | null;
  readonly rabattgruppe: string | null;
};

export async function zuordnungen(
  preisliste: string,
): Promise<readonly Preislistenzuordnung[]> {
  return rufe(`/artikel/preislisten/${preisliste}/zuordnungen`);
}

export async function zuordnungAnlegen(
  preisliste: string,
  ziel: { readonly kunde: string | null; readonly rabattgruppe: string | null },
): Promise<{ id: string }> {
  return rufe(`/artikel/preislisten/${preisliste}/zuordnungen`, {
    verfahren: 'POST',
    rumpf: ziel,
  });
}

export async function zuordnungLoeschen(
  preisliste: string,
  zuordnung: string,
): Promise<void> {
  await rufe(`/artikel/preislisten/${preisliste}/zuordnungen/${zuordnung}`, {
    verfahren: 'DELETE',
  });
}

/**
 * Die tatsächlich vergebenen Rabattgruppen.
 *
 * Aus dem Bestand und nicht aus einer Werteliste: `kondition.rabattgruppe`
 * ist an beiden Enden Freitext, und ein Tippfehler heißt dort stillschweigend
 * „kein Treffer".
 */
export async function rabattgruppen(): Promise<readonly string[]> {
  return rufe('/artikel/rabattgruppen');
}

export async function preislisten(auchBeendete = false): Promise<readonly Preisliste[]> {
  return rufe(`/artikel/preislisten${auchBeendete ? '?auchBeendete=true' : ''}`);
}

export async function preisauskunft(
  artikel: string,
  frage: {
    readonly kunde?: string | null;
    readonly vertriebsform?: Vertriebsform;
    readonly menge?: number;
    readonly stichtag?: string;
    /*
     * ANG-05, AA-011: Steht der Termin schon, bringt er seinen Standort als
     * Preisdimension mit — und beim Exklusivtermin die vereinbarte Pauschale
     * (DAT-08, PRD-46). Die Route nimmt ihn seit jeher; hier fehlte er.
     */
    readonly termin?: string | null;
  },
): Promise<Preisauskunft> {
  const fragen = new URLSearchParams();
  if (frage.kunde) fragen.set('kunde', frage.kunde);
  if (frage.vertriebsform) fragen.set('vertriebsform', frage.vertriebsform);
  if (frage.menge !== undefined) fragen.set('menge', String(frage.menge));
  if (frage.stichtag) fragen.set('stichtag', frage.stichtag);
  if (frage.termin) fragen.set('termin', frage.termin);
  return rufe(`/artikel/${artikel}/preisauskunft?${fragen.toString()}`);
}

/* ── Programm (PRD-25, PRD-31, PRD-32) ───────────────────────────────────── */

/** PRD-31: Eine Variante bekommt eine eigene Artikelnummer. */
export async function varianteAnlegen(
  stamm: string,
  eingabe: {
    readonly bezeichnung: string | null;
    readonly fahrzeugart: Fahrzeugart | null;
    readonly umfang: Umfang | null;
    readonly zielgruppe: Zielgruppe | null;
  },
): Promise<{ readonly id: string; readonly artikelnummer: string }> {
  return rufe(`/artikel/${stamm}/varianten`, { verfahren: 'POST', rumpf: eingabe });
}

/**
 * PRD-32: die Voraussetzungen ALS GANZES. Feldweise ginge nicht — die Zeile
 * der Variante ersetzt die des Stammprodukts vollstaendig, und eine Mischung
 * waere eine Kombination, die niemand geprueft hat.
 */
export async function voraussetzungenSetzen(
  artikel: string,
  eingabe: {
    readonly mindestalter: number | null;
    readonly fahrerlaubnisklassen: readonly string[];
    readonly vorkenntnisse: string | null;
    readonly fahrzeugMitbringen: boolean;
    readonly fahrzeugHinweis: string | null;
  },
): Promise<void> {
  await rufe(`/artikel/${artikel}/voraussetzungen`, { verfahren: 'PUT', rumpf: eingabe });
}

/** Die EIGENEN Voraussetzungen entfernen — die des Stammprodukts greifen wieder. */
export async function voraussetzungenLoeschen(artikel: string): Promise<void> {
  await rufe(`/artikel/${artikel}/voraussetzungen`, { verfahren: 'DELETE' });
}

export async function verpflegungSetzen(
  artikel: string,
  eingabe: {
    readonly portionen: number;
    readonly vorlaufStunden: number;
    readonly bistro: string | null;
    readonly plaetzeJePortion: number | null;
    readonly auswahl: readonly {
      readonly bezeichnung: string;
      readonly hinweis: string | null;
      readonly istVorgabe: boolean;
    }[];
  },
): Promise<void> {
  await rufe(`/artikel/${artikel}/verpflegung`, { verfahren: 'PUT', rumpf: eingabe });
}

/* ── E-Rechnung: Normangaben der Steuerschlüssel (RNG-13) ───────────────── */

/**
 * Die Kategorien nach UNCL5305 (BT-118) — mit dem, was sie steuerlich meinen.
 *
 * Aus dem Prozentsatz ist die Kategorie **nicht** ableitbar: Null Prozent kann
 * fünferlei heißen, und alle fünf sind verschiedene Sachverhalte. Deshalb
 * steht neben jedem Code, wann er gilt; wer ihn wählt, soll nicht raten
 * müssen.
 */
export const STEUERKATEGORIEN: readonly {
  readonly code: string;
  readonly bezeichnung: string;
  /** EN 16931 verlangt bei diesen Kategorien einen Grund (BT-120 oder BT-121). */
  readonly brauchtGrund: boolean;
}[] = [
  { code: 'S', bezeichnung: 'Regelsatz — Umsatzsteuer wird ausgewiesen', brauchtGrund: false },
  { code: 'Z', bezeichnung: 'Nullsatz — steuerbar zu 0 %', brauchtGrund: false },
  { code: 'E', bezeichnung: 'Steuerbefreit (z. B. § 4 UStG)', brauchtGrund: true },
  { code: 'AE', bezeichnung: 'Reverse Charge — der Empfänger schuldet die Steuer', brauchtGrund: true },
  { code: 'K', bezeichnung: 'Innergemeinschaftliche Lieferung', brauchtGrund: true },
  { code: 'G', bezeichnung: 'Ausfuhr außerhalb der EU', brauchtGrund: true },
  { code: 'O', bezeichnung: 'Nicht steuerbar — außerhalb des Anwendungsbereichs', brauchtGrund: true },
  { code: 'L', bezeichnung: 'Kanarische Inseln', brauchtGrund: true },
  { code: 'M', bezeichnung: 'Ceuta und Melilla', brauchtGrund: true },
];

export type Steuernorm = {
  readonly steuerkategorie: string | null;
  readonly befreiungsgrund: string | null;
  readonly befreiungsgrundCode: string | null;
};

/** Alle Steuerschlüssel — auch die abgelaufenen; alte Rechnungen tragen sie. */
export async function steuerschluesselAlle(): Promise<readonly Steuerschluessel[]> {
  return rufe('/artikel/steuerschluessel/alle');
}

export async function steuerschluesselNorm(
  schluessel: string,
  eingabe: Steuernorm,
): Promise<Steuerschluessel> {
  return rufe(`/artikel/steuerschluessel/${encodeURIComponent(schluessel)}`, {
    verfahren: 'PUT',
    rumpf: eingabe,
  });
}

/** RNG-13: was noch fehlt, bevor eine normkonforme E-Rechnung entstehen kann. */
export async function erechnungBereitschaft(): Promise<
  readonly { readonly gegenstand: string; readonly fehlt: string }[]
> {
  return rufe('/artikel/erechnung/bereitschaft');
}

/**
 * Eine Zeile der Trainingsdefinition (PRD-29, TAG-07).
 *
 * `dauerBloecke` ist die Übersteuerung am Produkt — `null` heißt „die
 * Regeldauer des Bausteins gilt". `dauerWirksam` ist die Zahl, die daraus
 * folgt.
 */
export type Folgezeile = {
  readonly reihenfolge: number;
  readonly baustein: string;
  readonly bezeichnung: string;
  readonly art: string;
  /** TAG-07: Bausteine derselben Phase sind frei vertauschbar. */
  readonly phase: number;
  readonly dauerBloecke: number | null;
  readonly dauerWirksam: number;
  readonly imLeistungsversprechen: boolean;
};

export type Bausteinfolge = {
  readonly artikel: {
    readonly id: string;
    readonly artikelnummer: string;
    readonly bezeichnung: string;
    readonly produktart: string;
  };
  readonly zeilen: readonly Folgezeile[];
  readonly leistungsmerkmale: readonly string[];
  readonly fassung: { readonly nummer: number; readonly gueltigAb: string } | null;
};

export async function bausteinfolge(artikel: string): Promise<Bausteinfolge> {
  return rufe(`/artikel/${encodeURIComponent(artikel)}/bausteinfolge`);
}

/**
 * PRD-29: die Folge setzen — und zwar GANZ.
 *
 * Die Reihenfolge ergibt sich aus der Position in der Liste; es gibt kein
 * Nummernfeld. Zwei Quellen für dieselbe Ordnung liefen beim Verschieben
 * auseinander.
 */
export async function bausteinfolgeSetzen(
  artikel: string,
  zeilen: readonly {
    readonly baustein: string;
    readonly phase: number;
    readonly dauerBloecke: number | null;
  }[],
): Promise<Bausteinfolge> {
  return rufe(`/artikel/${encodeURIComponent(artikel)}/bausteinfolge`, {
    verfahren: 'PUT',
    rumpf: { zeilen },
  });
}


/**
 * ============================================================================
 * DIE EVENTANGABEN (PRD-33)
 * ============================================================================
 *
 * Vier Dinge nennt die Anforderung, drei Herkünfte haben sie: Ort und Rahmen
 * stehen am Event, die zugekauften Leistungen daneben, die benötigten Räume
 * im Ressourcenbedarf. Die Antwort bringt alle drei zusammen, weil die Maske
 * sie zusammen zeigt — gepflegt werden sie dort, wo sie hingehören.
 */
export type Eventleistung = {
  readonly id: string;
  readonly bezeichnung: string;
  readonly anbieter: string | null;
  readonly bemerkung: string | null;
};

export type Eventangaben = {
  readonly artikel: string;
  readonly bezeichnung: string;
  readonly ort: string | null;
  readonly rahmen: string | null;
  readonly bemerkung: string | null;
  readonly leistungen: readonly Eventleistung[];
  /** Aus dem Ressourcenbedarf gelesen; hier nicht pflegbar (PRD-29). */
  readonly raeume: readonly { readonly bezeichnung: string; readonly anzahl: number }[];
};

export async function eventangaben(artikel: string): Promise<Eventangaben> {
  return rufe(`/artikel/${encodeURIComponent(artikel)}/event`);
}

export async function eventangabenSetzen(
  artikel: string,
  eingabe: {
    readonly ort: string | null;
    readonly rahmen: string | null;
    readonly bemerkung: string | null;
  },
): Promise<void> {
  await rufe(`/artikel/${encodeURIComponent(artikel)}/event`, {
    verfahren: 'PUT',
    rumpf: eingabe,
  });
}

/** Die zugekauften Leistungen als GANZE Liste — die Position ist die Reihenfolge. */
export async function eventleistungenSetzen(
  artikel: string,
  leistungen: readonly {
    readonly bezeichnung: string;
    readonly anbieter: string | null;
    readonly bemerkung: string | null;
  }[],
): Promise<void> {
  await rufe(`/artikel/${encodeURIComponent(artikel)}/event/leistungen`, {
    verfahren: 'PUT',
    rumpf: { leistungen },
  });
}

/**
 * C18 (0274): Betrag und Art eines Gutscheinartikels -- fester Betrag ODER
 * Rahmen von/bis. Erst danach laesst er sich buchbar stellen.
 */
export async function gutscheinBetragSetzen(
  artikel: string,
  angaben: { readonly art: string; readonly betrag: string | null; readonly von: string | null; readonly bis: string | null },
): Promise<void> {
  await rufe<void>(`/gutscheine/artikel/${artikel}`, { verfahren: 'PUT', rumpf: angaben });
}
